// ============================================================
// Practice Material — service layer (live Practice Studio API)
// ============================================================
import axios from 'axios';
import {
  PitchMap,
  CreatePracticePayload,
  ModuleUploadForm,
  Practice,
  PracticeModule,
  ModuleVariant,
  PitchTempoOption,
} from './practiceTypes';

// Practice Studio base (per API doc). TODO(api): move to an env var.
export const PRACTICE_STUDIO_BASE = process.env.REACT_APP_PRACTICE_STUDIO_BASE;
// AudioML service that generates the pitch-map bars from a recording.
export const AUDIOML_BASE = process.env.REACT_APP_AUDIOML_BASE;

// Endpoints from "API Documentation for LMS Practice Upload and Studio".
export const PRACTICE_API = {
  // 1. Create a Practice (folder)
  createFolder: `${PRACTICE_STUDIO_BASE}/api/practice-studio/practice/folder`,
  // 2a. Get an S3 path + signed URL for the recording
  presignedUrl: `${PRACTICE_STUDIO_BASE}/api/practice-studio/presigned-url`,
  // 2c. Create the Practice Module
  createModule: `${PRACTICE_STUDIO_BASE}/api/practice-studio/practice`,
  // List all practice folders
  list: `${PRACTICE_STUDIO_BASE}/api/practice-studio/artium-practice/folders`,
  // Top-level categories
  categories: `${PRACTICE_STUDIO_BASE}/api/practice-studio/artium-practice/categories`,
  // Sub-categories of a category
  subCategories: `${PRACTICE_STUDIO_BASE}/api/practice-studio/artium-practice/sub-categories/{categoryId}`,
  // List modules (+ their pitch maps) inside a folder
  folderResources: `${PRACTICE_STUDIO_BASE}/api/practice-studio/practice/artium-practice/folder/{folderId}/resources`,
  // GET the bars metadata (base pitch & tempo) for a module
  barsMetadata: `${PRACTICE_STUDIO_BASE}/api/practice-studio/practice/lms/{practiceId}/bars-metadata`,
  // PUT the edited bars metadata for a module
  saveBarsMetadata: `${PRACTICE_STUDIO_BASE}/api/practice-studio/practice/{practiceId}/bars-metadata`,
  // Mark an AI practice module as verified
  aiVerified: `${PRACTICE_STUDIO_BASE}/api/practice-studio/practice/ai-verified`,
  // Mark a specific AI practice resource as verified
  resourceAiVerified: `${PRACTICE_STUDIO_BASE}/api/practice-studio/practice/resource/ai-verified`,
};

// ---- Real Practice Studio API (exact contracts from the doc) ----

// 1. Create Practice (folder)
export type CreateFolderPayload = {
  folder_name: string;
  teacher_user_id: number;
  folder_path: string;
  parent_folder_id: number | null;
};
export const createPracticeFolderApi = async (payload: CreateFolderPayload) => {
  const res = await axios.post(PRACTICE_API.createFolder, payload);
  return res.data; // expected to include the new folder_id + folder_path
};

// 2a. Get presigned URL (returns the signed PUT url + the S3 recording path)
export type PresignedPayload = {
  recordingType: string; // e.g. "hindustani_durga/Pakad"
  userId: number;
  fileName: string;
};
export const getPresignedUrlApi = async (
  payload: PresignedPayload
): Promise<{ uploadUrl: string; recordingPath: string }> => {
  const res = await axios.post(PRACTICE_API.presignedUrl, payload);
  const d = res.data ?? {};
  // Real response: { signedURL, fileName, filePath, bucket, contentType }
  return {
    uploadUrl: d.signedURL ?? d.url ?? d.signedUrl ?? d.uploadUrl ?? d.presignedUrl,
    recordingPath: d.filePath ?? d.path ?? d.recordingPath ?? d.key ?? d.recording_path,
  };
};

// 2b. Upload the audio to the signed URL (reports 0-100 progress)
export const putAudioToSignedUrl = async (
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void
) => {
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type || 'audio/mpeg' },
    skipAuth: true,
    onUploadProgress: (e: any) => {
      if (!onProgress) return;
      const total = e.total || file.size;
      if (total) onProgress(Math.min(100, Math.round((e.loaded / total) * 100)));
    },
  } as any);
};

// 2c. Create the Practice Module
export type CreateModulePayload = {
  practice_name: string;
  description: string;
  created_by: string;
  recording_path: string;
  recording_name: string;
  pitch_key: string;
  tempo: number;
  teacher_user_id: number;
  folder_id: number;
  folder_path: string;
};
export const createPracticeModuleApi = async (payload: CreateModulePayload) => {
  const res = await axios.post(PRACTICE_API.createModule, payload);
  return res.data;
};

// ---- Defaults until the parent-folder lookup API is available ----
// TODO(api): replace with values from the parent-folder lookup ("Raj will share").
export const DEFAULT_PARENT_FOLDER_ID = 13;
export const DEFAULT_FOLDER_BASE_PATH = '/practice';
const TEACHER_USER_ID = 0; // admin-created practices

// ---- Practices ----
// Maps a backend "folder" to a UI Practice.
const folderToPractice = (f: any): Practice => ({
  id: f.folder_id ?? f.id ?? f.folder_path,
  name:
    f.folder_name ??
    f.name ??
    String(f.folder_path ?? '').split('/').filter(Boolean).pop() ??
    'Untitled',
  folderId: Number(f.folder_id ?? f.id) || undefined,
  folderPath: f.folder_path ?? f.folderPath,
  moduleCount: f.file_count ?? f.module_count ?? f.moduleCount,
  description: f.description,
  createdBy: String(f.teacher_user_id ?? f.created_by ?? ''),
});

// LIVE: lists all practice folders.
// Response shape: { userPractice: [ { folder_id, folder_name, folder_path, file_count, ... } ] }
export const getPractices = async (): Promise<Practice[]> => {
  const res = await axios.get(PRACTICE_API.list);
  const raw =
    res.data?.userPractice ?? res.data?.folders ?? res.data?.practices ?? res.data ?? [];
  return Array.isArray(raw) ? raw.map(folderToPractice) : [];
};

// LIVE: top-level categories. Response: { categories: [ {folder_id, folder_name, ...} ] }
export const getCategories = async (courseId?: string | number): Promise<Practice[]> => {
  const res = await axios.get(PRACTICE_API.categories, {
    params: courseId ? { courseId } : undefined,
  });
  const raw = res.data?.categories ?? res.data ?? [];
  return Array.isArray(raw) ? raw.map(folderToPractice) : [];
};

// LIVE: sub-categories of a category. Response: { subCategories: [ {...} ] }
export const getSubCategories = async (
  categoryId: string | number,
  courseId?: string | number
): Promise<Practice[]> => {
  const url = PRACTICE_API.subCategories.replace('{categoryId}', String(categoryId));
  const res = await axios.get(url, {
    params: { is_lms: true, ...(courseId ? { courseId } : {}) },
  });
  const raw = res.data?.subCategories ?? res.data ?? [];
  return Array.isArray(raw) ? raw.map(folderToPractice) : [];
};

// LIVE: creates a practice (backend "folder") via the Practice Studio API.
// The folder API only accepts folder_name + path/parent; the extra practice
// fields (description/courses/levels/type) are kept on the returned object for
// the UI until the backend supports them.
export const createPractice = async (payload: CreatePracticePayload): Promise<Practice> => {
  const folderPath = payload.folderPath ?? `${DEFAULT_FOLDER_BASE_PATH}/${payload.name}`;
  const parentFolderId =
    payload.parentFolderId !== undefined ? payload.parentFolderId : DEFAULT_PARENT_FOLDER_ID;
  const res = await createPracticeFolderApi({
    folder_name: payload.name,
    teacher_user_id: TEACHER_USER_ID,
    folder_path: folderPath,
    parent_folder_id: parentFolderId,
  });
  const folderId =
    res?.folder_id ?? res?.id ?? res?.folder?.folder_id ?? res?.data?.folder_id;
  return {
    id: folderId ?? folderPath,
    name: payload.name,
    folderId,
    folderPath,
    createdBy: payload.createdBy,
    moduleCount: 0,
    description: payload.description,
    courseIds: payload.courseIds,
    levels: payload.levels,
    practiceType: payload.practiceType,
  };
};

// Folder resources are paged. `page` is 1-based; default page size matches the API (10).
export const PRACTICE_MODULES_PAGE_SIZE = 10;

export type PracticeModulesPage = {
  modules: PracticeModule[];
  page: number;
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
};

// ---- Modules ----
// LIVE: lists modules inside a folder, including each module's pitch map.
export const getPracticeModules = async (
  folderId: string | number,
  courseId?: string | number,
  page = 1,
  pageSize = PRACTICE_MODULES_PAGE_SIZE
): Promise<PracticeModulesPage> => {
  const url = PRACTICE_API.folderResources.replace('{folderId}', String(folderId));
  const res = await axios.get(url, {
    params: {
      is_lms: true,
      page,
      pageSize,
      ...(courseId ? { courseId } : {}),
    },
  });
  const resources: any[] = res.data?.resources ?? [];
  const currentPage = Number(res.data?.page_no) || page;
  const totalPages = Number(res.data?.total_no_pages) || 1;
  const totalCount = Number(res.data?.total_count) || resources.length;
  const modules: PracticeModule[] = resources.map((r) => {
    const list: any[] = r.resource_list ?? [];
    const withBars = list.find((x) => x?.bars_metadata?.bars_data) ?? list[0];
    const pm = withBars?.bars_metadata;
    const orig = list.find((x) => x?.recording_type === 'original') ?? list[0];
    const pitchTempoOptions = Array.from(
      list.reduce((acc, item) => {
        const pitchKey = item?.pitch_key;
        const tempo = Number(item?.tempo);
        if (pitchKey && Number.isFinite(tempo)) {
          acc.set(`${pitchKey}-${tempo}`, { pitchKey, tempo });
        }
        return acc;
      }, new Map<string, PitchTempoOption>()).values()
    ) as PitchTempoOption[];
    return {
      id: r.practice_id,
      name: r.practice_name,
      description: r.description,
      pitch: r.original_pitch,
      tempo: r.original_tempo != null ? Number(r.original_tempo) : '',
      isEnabledForAI: !!r.is_ai_recording,
      moduleType: r.module_type ?? r.practice_module_type ?? '',
      aiVerified: !!r.is_ai_practice_verified,
      pitchMap: pm && pm.bars_data ? (pm as PitchMap) : undefined,
      recordingPath: orig?.recording_path,
      aiRecordingType:
        orig?.ai_recording_type && orig.ai_recording_type !== 'none'
          ? orig.ai_recording_type
          : undefined,
      pitchTempoOptions,
    };
  });
  return {
    modules,
    page: currentPage,
    totalPages,
    totalCount,
    hasMore: currentPage < totalPages,
  };
};

// ---- Module upload (LIVE 3-step flow per API doc) ----
export type UploadModuleArgs = {
  folderId: number;
  folderPath: string;
  folderName: string;
  form: ModuleUploadForm;
  file: File;
  createdBy: string;
  onProgress?: (percent: number) => void;
};

export const uploadPracticeModule = async ({
  folderId,
  folderPath,
  folderName,
  form,
  file,
  createdBy,
  onProgress,
}: UploadModuleArgs): Promise<{ moduleId: string | number; fileUrl: string }> => {
  const userId = Number(createdBy) || 0;
  // TODO(api): confirm how recordingType is derived (doc example "hindustani_durga/Pakad").
  const recordingType = `${folderName}/${form.name}`;

  // 2a) presigned URL + S3 recording path
  const { uploadUrl, recordingPath } = await getPresignedUrlApi({
    recordingType,
    userId,
    fileName: file.name,
  });
  if (!uploadUrl || !recordingPath) {
    throw new Error('presigned-url response missing signedURL/filePath');
  }

  // 2b) upload the audio
  await putAudioToSignedUrl(uploadUrl, file, onProgress);

  // 2c) create the module
  const recordingName = recordingPath.split('/').pop() || file.name;
  const res = await createPracticeModuleApi({
    practice_name: form.name,
    description: form.description,
    created_by: createdBy,
    recording_path: recordingPath,
    recording_name: recordingName,
    pitch_key: form.pitch || '',
    tempo: Number(form.tempo) || 0,
    teacher_user_id: TEACHER_USER_ID,
    folder_id: folderId,
    folder_path: folderPath,
  });

  return {
    moduleId: res?.id ?? res?.module_id ?? res?.practice_id ?? `mod-${recordingName}`,
    fileUrl: recordingPath,
  };
};

// LIVE: generate the pitch-map bars from a recording via the AudioML service.
export type GenerateBarsArgs = {
  recordingPath: string;
  practiceModuleType: string; // e.g. "piano_sing_along"
  aiRecordingType: string; // e.g. "sing_along"
  studentBarStartTime: number;
};
export const generateBarsData = async (args: GenerateBarsArgs): Promise<PitchMap> => {
  const token = localStorage.getItem('authToken') || '';
  const authorization = /^bearer /i.test(token) ? token : `Bearer ${token}`;
  const res = await axios.post(
    `${AUDIOML_BASE}/generate_bars_data`,
    {
      recording_path: args.recordingPath,
      practice_module_type: args.practiceModuleType,
      ai_recording_type: args.aiRecordingType,
      student_bar_start_time: args.studentBarStartTime,
    },
    // skipAuth: send our own Bearer header instead of the app's raw-token interceptor.
    { headers: { Authorization: authorization }, skipAuth: true } as any
  );
  const d = res.data ?? {};
  const bm = d.bars_metadata ?? d;
  if (!Array.isArray(bm?.bars_data)) {
    const keys = d && typeof d === 'object' ? Object.keys(d).join(', ') : typeof d;
    throw new Error(`generate_bars_data returned no bars_data (keys: ${keys})`);
  }
  return bm as PitchMap;
};

// GET all editable variants (resources) of a module + their bars metadata.
export const getModuleVariants = async (
  practiceId: string | number,
  pitchTempo?: PitchTempoOption | null
): Promise<ModuleVariant[]> => {
  const url = PRACTICE_API.barsMetadata.replace('{practiceId}', String(practiceId));
  const res = await axios.get(url, {
    params: pitchTempo ? { pitch: pitchTempo.pitchKey, tempo: pitchTempo.tempo } : undefined,
  });
  const resources: any[] = res.data?.resources ?? [];
  return resources
    .filter((r) => !r.is_deleted)
    .map((r) => ({
      id: r.id,
      recordingType: r.recording_type ?? '',
      aiRecordingType: r.ai_recording_type ?? 'none',
      pitchKey: r.pitch_key,
      tempo: r.tempo,
      transposedSemitones: r.transposed_semitones,
      recordingPath: r.recording_path,
      isAiPracticeVerified: !!r.is_ai_practice_verified,
      pitchMap: (r.bars_metadata?.bars_data
        ? r.bars_metadata
        : { bars_data: [] }) as PitchMap,
    }));
};

// Get a presigned GET URL to stream a recording (private S3 bucket).
export const getRecordingUrl = async (recordingPath: string): Promise<string> => {
  const fileName = recordingPath.split('/').pop() || recordingPath;
  const res = await axios.get(PRACTICE_API.presignedUrl, {
    params: { fileName, filePath: recordingPath },
  });
  return res.data?.signedURL ?? res.data?.url ?? '';
};

// LIVE: save the edited bars metadata for a module, targeting a variant by ai_recording_type.
// Body shape (per API): { bars_metadata: { bars_data, ... }, ai_recording_type }
export type SaveTarget = {
  aiRecordingType?: string;
  pitchTempo?: PitchTempoOption | null;
};
export const saveVerifiedAI = async (
  moduleId: string | number,
  result: PitchMap,
  target?: SaveTarget
): Promise<void> => {
  const bars_metadata: any = { bars_data: result.bars_data };
  if (result.student_sections_data) bars_metadata.student_sections_data = result.student_sections_data;
  if (result.student_section_weights)
    bars_metadata.student_section_weights = result.student_section_weights;
  const url = PRACTICE_API.saveBarsMetadata.replace('{practiceId}', String(moduleId));
  const body: any = { bars_metadata };
  if (target?.aiRecordingType) body.ai_recording_type = target.aiRecordingType;
  if (target?.pitchTempo) {
    body.pitch = target.pitchTempo.pitchKey;
    body.tempo = target.pitchTempo.tempo;
  }
  await axios.put(url, body);
};

export const setPracticeVerification = async (
  practiceId: string | number,
  isVerified: boolean
): Promise<void> => {
  await axios.patch(PRACTICE_API.aiVerified, {
    practice_id: Number(practiceId),
    is_ai_practice_verified: isVerified,
  });
};

export const setResourceVerification = async (
  resourceId: string | number,
  isVerified: boolean
): Promise<void> => {
  await axios.patch(PRACTICE_API.resourceAiVerified, {
    resource_id: Number(resourceId),
    is_ai_practice_verified: isVerified,
  });
};
